/**
 * Seo component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/use-static-query/
 */

import * as React from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "gatsby-plugin-react-i18next";

import activeProducts, { GATSBY_CONFIGURATION } from "../../config/activeProducts";
import { getPageLastModified } from "../../config/pageLastModified";
import { enhanceMerchantListingSchema } from "../../helpers/merchantListingSchemaHelper";
import { createSeoHeadTranslator } from "../../helpers/seoHeadTranslation";
import { isProductOrCategoryPage, withCurrentYearInTitle, withDateModified } from "../../helpers/seoFreshness";

const languageImages = {
  en: "boleron/product/product_card_main_en.jpg"
};

const defaultSupportedLanguages = {
  boleron: ["bg", "en"],
  "boleron-ro": ["ro", "en"]
};

const localeByLanguage = {
  bg: "bg_BG",
  en: "en_US",
  ro: "ro_RO"
};

const trimLeadingSlash = value => value.replace(/^\/+/, "");

const getPathLanguage = pathname => pathname?.match(/^\/([a-z]{2})(?:\/|$)/)?.[1] || null;

const normalizePagePath = pathname => {
  const pathWithoutLanguage = (pathname || "/").replace(/^\/[a-z]{2}(?=\/|$)/, "") || "/";
  const pathWithoutTrailingSlash = pathWithoutLanguage.replace(/\/$/, "");

  return pathWithoutTrailingSlash || "/";
};

const getCanonicalPath = canonical => {
  if (!canonical || canonical === "/") {
    return "";
  }
  return trimLeadingSlash(canonical);
};

const getLocalizedUrl = ({ companyURL, canonical, language, defaultLanguage, supportedLanguages }) => {
  const canonicalPath = getCanonicalPath(canonical);
  const shouldPrefixLanguage = supportedLanguages.includes(language) && language !== defaultLanguage;
  const path = shouldPrefixLanguage ? `${language}/${canonicalPath}` : canonicalPath;

  return new URL(path, companyURL).href;
};

const getAlternateLinks = ({ companyURL, canonical, defaultLanguage, supportedLanguages }) => {
  if (!canonical || supportedLanguages.length < 2) {
    return [];
  }

  const languageLinks = supportedLanguages.map(language => ({
    hrefLang: language,
    href: getLocalizedUrl({ companyURL, canonical, language, defaultLanguage, supportedLanguages })
  }));

  return [
    ...languageLinks,
    {
      hrefLang: "x-default",
      href: getLocalizedUrl({ companyURL, canonical, language: defaultLanguage, defaultLanguage, supportedLanguages })
    }
  ];
};

const getMerchantListingCountryCode = companyURL => {
  if (GATSBY_CONFIGURATION === "boleron-ro" || companyURL?.includes("//ro.")) {
    return "RO";
  }

  return "BG";
};

const SeoTenant = ({ ...props }) => {
  const { t: contextT } = useTranslation();
  const { pagesSEO, defaultSEO: defaults, websiteName, companyURL, showMetaDataAndSchemas, showJsonLdSchemas = showMetaDataAndSchemas } = activeProducts.tenantSettings;
  const { location, isNoIndex, isLoadingSeo, isHeadApi } = props;
  const supportedLanguages = activeProducts.tenantSettings.supportedLanguages || defaultSupportedLanguages[GATSBY_CONFIGURATION] || [];
  const defaultLanguage = activeProducts.tenantSettings.defaultLanguage || defaults.lang;
  const pathLanguage = getPathLanguage(location?.pathname);
  const pageContextLanguage = props.pageContext?.i18n?.language;
  const propLanguage = props.lang && (!supportedLanguages.length || supportedLanguages.includes(props.lang)) ? props.lang : null;
  const lang =
    (pathLanguage && supportedLanguages.includes(pathLanguage) && pathLanguage) ||
    (pageContextLanguage && supportedLanguages.includes(pageContextLanguage) && pageContextLanguage) ||
    propLanguage ||
    defaultLanguage;
  const t = isHeadApi
    ? createSeoHeadTranslator({
        localeEdges: props.data?.locales?.edges,
        language: lang,
        fallbackT: contextT
      })
    : contextT;
  const translatedPagesSEO = pagesSEO(t);
  const pageProps = translatedPagesSEO.find(p => {
    const strippedPathname = normalizePagePath(location?.pathname);
    const strippedPageName = p.pageName.replace(/\/$/, "") || "/";
    return strippedPageName === strippedPathname;
  });

  // Temporary fix : "location.pathname" returns a path name with en/ whitch we don't have in pagesSEO hence pageProps is undefined
  // Probably a fix would be for vivacom for us to remove the en  paths from graphql
  // При добавяне на данни винаги трябва да се добавя във vivacom.js и boleron.js

  if (isLoadingSeo) {
    if (isHeadApi) {
      return (
        <>
          <link rel="canonical" href={`${companyURL}init-workflow`} />
          <meta property="og:url" content={`${companyURL}init-workflow`} />
          <meta data-react-helmet="true" name="robots" content="noindex, nofollow" />
          <meta property="twitter:title" content={t("key24493", "Моля изчакайте...")} />
        </>
      );
    }
    return (
      <Helmet>
        <link rel="canonical" href={`${companyURL}init-workflow`} />
        <meta property="og:url" content={`${companyURL}init-workflow`} />
        <meta data-react-helmet="true" name="robots" content="noindex, nofollow" />
        <meta property="twitter:title" content={t("key24493", "Моля изчакайте...")} />
      </Helmet>
    );
  }

  if (!pageProps && isNoIndex) {
    return (
      <Helmet>
        <meta data-react-helmet="true" name="robots" content="noindex, nofollow" />
      </Helmet>
    );
  }

  const metaDescription = pageProps?.description || defaults?.description;
  const defaultTitle = pageProps?.title || defaults?.title;
  const title = GATSBY_CONFIGURATION === "boleron" ? withCurrentYearInTitle(defaultTitle, pageProps?.pageName) : defaultTitle;
  const ldSchemal = props.schemaMarkup || pageProps?.schemaMarkup;
  const enhancedLdSchema =
    showJsonLdSchemas &&
    enhanceMerchantListingSchema(ldSchemal, {
      countryCode: getMerchantListingCountryCode(companyURL)
    });
  const ogLocale = localeByLanguage[lang] || localeByLanguage[defaultLanguage] || "en_US";
  const metaImage = languageImages[lang] && pageProps?.image ? languageImages[lang] : pageProps?.image;
  const canonicalUrl = pageProps?.canonical
    ? getLocalizedUrl({
        companyURL,
        canonical: pageProps.canonical,
        language: lang,
        defaultLanguage,
        supportedLanguages
      })
    : null;
  const schemaWithDateModified =
    GATSBY_CONFIGURATION === "boleron" && isProductOrCategoryPage(pageProps?.pageName) && canonicalUrl
      ? withDateModified({ schema: enhancedLdSchema, canonicalUrl, title, dateModified: getPageLastModified(pageProps.pageName) })
      : enhancedLdSchema;
  const alternateLinks = getAlternateLinks({
    companyURL,
    canonical: pageProps?.canonical,
    defaultLanguage,
    supportedLanguages
  });
  const alternateLocales = alternateLinks.map(link => localeByLanguage[link.hrefLang]).filter(locale => locale && locale !== ogLocale);

  // Case for statick SSR build
  if (!pageProps) {
    return;
  }

  if (isHeadApi) {
    return (
      <>
        <html lang={lang} />
        <title>{title}</title>
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        {alternateLinks.map(link => (
          <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
        ))}
        <meta property="og:locale" content={ogLocale} />
        {alternateLocales.map(locale => (
          <meta key={locale} property="og:locale:alternate" content={locale} />
        ))}
        <meta property="og:site_name" content={websiteName || ""} />
        <meta name="keywords" content={pageProps?.keywords || ""} />
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={title || ""} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={metaImage ? `${companyURL}images/${metaImage}` : ""} />
        {metaImage && <meta property="og:image:width" content="792" />}
        {metaImage && <meta property="og:image:height" content="511" />}
        {metaImage && <meta property="og:image:alt" content={title || websiteName || ""} />}

        {showMetaDataAndSchemas && <meta property="twitter:card" content="summary_large_image" />}
        {showMetaDataAndSchemas && <meta property="twitter:title" content={title || ""} />}
        {showMetaDataAndSchemas && <meta property="twitter:description" content={metaDescription} />}
        {showMetaDataAndSchemas && <meta property="twitter:image" content={metaImage ? `${companyURL}images/${metaImage}` : ""} />}
        {showMetaDataAndSchemas && <meta name="apple-itunes-app" content="app-id=1476539247" />}
        {showMetaDataAndSchemas && <meta property="fb:app_id" content="431182437575375" />}

        <link rel="dns-prefetch" href="https://fonts.googleapis.com" />

        {isNoIndex && <meta data-react-helmet="true" name="robots" content="noindex, nofollow" />}
        {showJsonLdSchemas && schemaWithDateModified && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(schemaWithDateModified).replace(/</g, "\\u003c")
            }}
          />
        )}
      </>
    );
  }

  return (
    <Helmet
      htmlAttributes={{
        lang
      }}
      title={title}
    >
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      {alternateLinks.map(link => (
        <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
      ))}
      <meta property="og:locale" content={ogLocale} />
      {alternateLocales.map(locale => (
        <meta key={locale} property="og:locale:alternate" content={locale} />
      ))}
      <meta property="og:site_name" content={websiteName || ""} />
      <meta name="keywords" content={pageProps?.keywords || ""} />
      <meta name="description" content={metaDescription} />
      <meta property="og:title" content={title || ""} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={metaImage ? `${companyURL}images/${metaImage}` : ""} />
      {metaImage && <meta property="og:image:width" content="792" />}
      {metaImage && <meta property="og:image:height" content="511" />}
      {metaImage && <meta property="og:image:alt" content={title || websiteName || ""} />}

      {showMetaDataAndSchemas && <meta property="twitter:card" content="summary_large_image" />}
      {showMetaDataAndSchemas && <meta property="twitter:title" content={title || ""} />}
      {showMetaDataAndSchemas && <meta property="twitter:description" content={metaDescription} />}
      {showMetaDataAndSchemas && <meta property="twitter:image" content={metaImage ? `${companyURL}images/${metaImage}` : ""} />}
      {showMetaDataAndSchemas && <meta name="apple-itunes-app" content="app-id=1476539247" />}
      {showMetaDataAndSchemas && <meta property="fb:app_id" content="431182437575375" />}

      <link rel="dns-prefetch" href="https://fonts.googleapis.com" />

      {isNoIndex && <meta data-react-helmet="true" name="robots" content="noindex, nofollow" />}
      {showJsonLdSchemas && schemaWithDateModified && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schemaWithDateModified).replace(/</g, "\\u003c")
          }}
        />
      )}
    </Helmet>
  );
};

SeoTenant.defaultProps = {
  meta: [],
  description: ``
};

export default SeoTenant;
